/**
 * Attribute marking every native `<audio>` / `<video>` the toolkit
 * renders on a message surface, so opening one player can silence the
 * others.
 *
 * stream-chat-react's `AudioPlayerPool` — what `useActiveAudioPlayer()`
 * reads — only knows the players *it* mounts, i.e. voice recordings.
 * Everything the toolkit draws itself is a plain native element that
 * never registers there: `MessageAttachment.Audio`, a playable
 * `LinkAttachment` card, and `AttachmentCard`'s `MediaPlayer`. Pausing
 * the pool alone therefore left thread audio sounding underneath an
 * opened video viewer on the production `StreamAttachmentMessage` path,
 * which is exactly the surface the mutual exclusion exists for
 * (MES-1353).
 */
export const NATIVE_PLAYER_ATTRIBUTE = 'data-mes-native-player'

/**
 * Spread onto a native player element to enrol it. Keyed off the
 * constant so the marker and the query below can't drift apart.
 */
export const nativePlayerProps = { [NATIVE_PLAYER_ATTRIBUTE]: '' } as const

/**
 * Pauses every enrolled player except `except`.
 *
 * No `paused` check: pausing an already-paused element is a no-op per
 * spec, and reading `paused` first would only add a branch that jsdom
 * answers differently from a browser.
 */
export const pauseNativeMediaPlayers = (
  except?: HTMLMediaElement | null
): void => {
  if (typeof document === 'undefined') return

  document
    .querySelectorAll<HTMLMediaElement>(`[${NATIVE_PLAYER_ATTRIBUTE}]`)
    .forEach((player) => {
      if (player !== except) player.pause()
    })
}
